import intersection from 'lodash-es/intersection';
import { ru as ruLocale } from 'date-fns/locale/ru';
import { uk as ukLocale } from 'date-fns/locale/uk';
import { enUS as enLocale } from 'date-fns/locale/en-US';
import { pl as plLocale } from 'date-fns/locale/pl';
import { es as esLocale } from 'date-fns/locale/es';
import buildAbilities from 'ability.config';
import Currency from 'Js/api/v1/model/Currency';
import User from 'Js/api/v1/model/User';
import Boost from 'Js/api/v1/model/Boost';
import ajax from 'Js/api/lib/ajax';

/**
 * Глобальные переменные проекта.
 * В этом модуле формируются объекты:
 * - avtopro (записывается в window.avtopro) с данными о пользователе и окружении;
 * - ability с конфигурацией прав текущего пользователя.
 */

const parseGlobals = sourceId => {
    try {
        if (__SERVER__) {
            switch (sourceId) {
                case 'js-globals':
                    return [JSON.parse(globalThis.baseModel), {}];

                case 'js-features':
                    return [JSON.parse(globalThis.featureFlags)];
            }
        } else {
            const source = document.getElementById(sourceId);
            if (source) {
                return [JSON.parse(source.textContent), source.dataset];
            }
        }
    } catch (e) {
        console.error(e);
    }
    throw new TypeError('Unable to create global "avtopro" object.');
};

const globals = new (function Avtopro() {
    const [
        {
            InfoUser,
            UserProfile,
            SellerSite,
            SellerInfo,
            Location,
            AccessRoles,
            UrlReferrer,
            BoostDetailsDTO
        },
        { isEmulatingUser }
    ] = parseGlobals('js-globals');

    this.user = new User(
        // Информация о пользователе присутствует всегда,
        // даже если пользователь — Гость (id = 75)
        InfoUser.Login,
        {
            ...(UserProfile
                ? {
                      iSellerCurrency: UserProfile.Currency_by_phone,
                      sellerCountry: UserProfile.Country_by_phone,
                      // TODO: это поле должно быть удалено из класса User,
                      // а проверки, которые его использовали, должны быть
                      // реализованы через ability.config
                      // isLimited: getGlobalAttr('data-user-islimited'),
                      dialoguesCount: UserProfile.PersonalMessageCount || 0,
                      MailingLang: UserProfile.MailingLang,
                      // Система скидок
                      Discounts: UserProfile.Discounts,
                      // Підключен функціонал телеграм-чату
                      IsActivatedTelegramChat:
                          UserProfile.IsActivatedTelegramChat,
                      Email: UserProfile.Email,
                      IsPhoneConfirmed: Boolean(UserProfile.Mobile_phone)
                  }
                : undefined),
            // Профиль есть только у залогиненного пользователя
            ...SellerInfo,
            Id: InfoUser.Id,
            isBlocked: InfoUser.Block == 1,
            IsSeller: InfoUser.IsExistLicens,
            groupId: InfoUser.GroupId,
            becomeSellerDate: UserProfile?.BecomeSellerDate ?? null
        }
    );

    this.boostInfo = BoostDetailsDTO ? new Boost(BoostDetailsDTO) : null;
    // Данные об принятых настройках куки
    this.cookieConsent = {
        isAdvertisingCookiesAccepted: UserProfile?.IsEssentialCookiesAccepted,
        isAnalyticsCookiesAccepted: UserProfile?.IsPerformanceCookiesAccepted,
        isFunctionalityCookiesAccepted:
            UserProfile?.IsFunctionalityCookiesAccepted,
        isPersonalizationCookiesAccepted:
            UserProfile?.IsTargetingCookiesAccepted
    };

    /**
     * Пользователь (админ, саппорт) находится под профилем другого пользователя.
     * См. APFunctions.IsEmulatingUser на бекенде.
     */
    this.isEmulatingUser =
        typeof isEmulatingUser == 'string'
            ? isEmulatingUser == 'true'
            : Boolean(isEmulatingUser);

    /**
     * Данные об используемой региональной площадке.
     */
    this.site = {
        id: SellerSite.Id,
        link: SellerSite.Link,
        name: SellerSite.SiteName,
        logo: SellerSite.Logo,
        // TODO: принимать значение LanguageСorrected
        language: (lang => {
            if (lang == 'ua') return 'uk';
            return lang;
        })(SellerSite.Language?.toLowerCase()),
        currencies: SellerSite.Currencies.map(c => new Currency(c)),
        // Массив ISO-кодов стран, соответствующих региональной площадке
        // По умолчанию страны отсортированы в алфавитном порядке,
        // но это нелогично: большинство пользователей из Украины, России и т. д.,
        // поэтому сортируем по статически заданным приоритетам, а в будущем
        // можно было бы перенести управление приоритетами на бекенд
        countries: (index =>
            // Бекенд передаёт код ISO: название страны, но нам нужен только код
            Object.keys(SellerSite.Countries)
                // Было бы лучше привести коды к нижнему регистру, т. к. библиотеки
                // например country-telephone-data, в основном используют нижний регистр.
                // Но в geoLocation у нас сейчас созданный на бекенде объект
                // со свойством CountryISO, где код указан в верхнем регистре,
                // так что пока оставляем верхний регистр везде.
                .map(iso => iso.toUpperCase())
                .sort(
                    (iso1, iso2) =>
                        (index[iso1] || Infinity) - (index[iso2] || Infinity)
                ))({
            UA: 1,
            ES: 2,
            PL: 3
            // ... дальше страны идут в алфавитном порядке
        })
    };

    /**
     * Локаль date-fns для форматирования дат.
     * Определяется на основе языка региональной площадки.
     */
    this.locale = (() => {
        switch (this.site.language.toLowerCase()) {
            case 'ru':
                return ruLocale;
            case 'es':
                return esLocale;
            case 'ua':
            case 'uk':
                return ukLocale;
            case 'pl':
                return plLocale;
            case 'en':
            default:
                return enLocale;
        }
    })();

    /**
     * Геолокация пользователя.
     * Модель объекта Location не совпадает моделью гео-объекта
     * и должна быть переделана на бекенде, после чего можно
     * будет применить JS-класс GeoObject.
     */
    this.geoLocation = Location; // TODO new GeoObject(Location);

    /**
     * URL, с которого пришел пользователь
     */
    this.urlReferrer = UrlReferrer || undefined;

    /**
     * Роли текущего пользователя.
     */
    this.userRoles = new Set(AccessRoles);

    /**
     * Глобальные Feature Flags проекта:
     * ориентируемся на них для включения/выключения разного функционала.
     * Избегаем проверок по домену/языку/id сайта.
     * Флаги настраиваются на бекенде в классе FeatureFlags.cs,
     * можно добавлять свои флаги, или менять условия в имеющихся.
     */
    this.featureFlags = new Map(Object.entries(parseGlobals('js-features')[0]));

    /**
     * ISO-код страны, по которой будет работать поиск по госномерам.
     *
     * @TODO все проверки должны быть заложены внутрь флага IsRegnumSearchAvailable.
     * Сейчас у флагов нет доступа к геолокации и sellerCountry, поэтому значение
     * IsRegnumSearchAvailable служит только для первой проверки: если он false,
     * значит поиск по госномеру полностью отключен. Если же он true, то условие
     * доуточняется на фронте.
     */
    this.regnumSearchCountryIso = (() => {
        if (!this.featureFlags.get('IsRegnumSearchAvailable')) {
            return null;
        }

        const possibleLocations = ['UA', 'ES'];

        // Прибираємо логіку визначення локації юзера для avto.pro,
        // так як зараз цей майдачник використовується в більшості для України.
        // Для домена avto.pro дополнительное условие:
        // поиск доступен только если пользователь находится в одной из possibleLocations.
        // if (this.site.id == 1) {
        //     if (possibleLocations.includes(this.geoLocation.CountryISO)) {
        //         return this.geoLocation.CountryISO;
        //     }
        //     if (possibleLocations.includes(this.user.sellerCountry)) {
        //         return this.user.sellerCountry;
        //     }
        //     return null;
        // }

        // Т. к. площадка может быть (обычно так и есть) привязана сразу к нескольким странам,
        // например ['ES', 'PL', 'AT', 'AR', 'BE'], то не остаётся ничего лучшего,
        // чем искать пересечение массивов стран и possibleLocations и брать первый элемент.
        return intersection(possibleLocations, this.site.countries)[0] ?? null;
    })();

    /**
     * TODO: выяснить нужен ли ещё этот функционал.
     */
    this.esputnik = {
        becomeseller: () => {
            return ajax.post({
                url: '/api/v1/esputnik/contacts/bulkupdate/becomeseller'
            });
        },
        checkphone: () => {
            return ajax.post({
                url: '/api/v1/esputnik/contacts/bulkupdate/checkphone'
            });
        }
    };
    if (Object.freeze) {
        Object.freeze(this);
    }
})();

export default globals;

/**
 * Права пользователя, вычисленные на основании ролей
 * и возможностей данной региональной площадки.
 * См. js/ability.config.js
 */
export const ability = buildAbilities(
    globals.user,
    globals.userRoles,
    globals.isEmulatingUser,
    globals.featureFlags
);

if (!__SERVER__) {
    window.avtopro = globals;
}
